import React, { useCallback, useEffect, useState } from 'react';
import { Gauge, ChevronDown, ChevronRight, Download, RefreshCw } from 'lucide-react';
import { reportsService } from '../services/reports.service';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { downloadCsv, flattenMonthVendorRows } from '../utils/csvExport';

// Month-wise vendor SLA from vendor_job_metrics (one row per completed job),
// with a per-vendor breakdown. Month/grand totals are job-weighted averages.
// acceptance / arrival / completion / SLA score are 0-100; rating is 1-5.

const RANGE_OPTS = [
    { label: 'Last 6 months', months: 6 },
    { label: 'Last 12 months', months: 12 },
    { label: 'Last 24 months', months: 24 },
];

const pct = (v) => (v == null ? '—' : `${v.toFixed(1)}%`);
const num = (v, nd = 1) => (v == null ? '—' : v.toFixed(nd));
const secs = (v) => (v == null ? '—' : `${Math.round(v)}s`);

const SLA_CSV_HEADERS = [
    'Month',
    'Level',
    'Vendor',
    'Jobs',
    'Avg response (sec)',
    'Acceptance %',
    'On-time %',
    'Completion %',
    'Avg rating',
    'SLA score',
];

function slaCsvRows(data) {
    return flattenMonthVendorRows(
        data?.months,
        (month, t) => [
            month,
            'Month total',
            '',
            t.jobs ?? 0,
            t.avg_response_sec ?? '',
            t.acceptance_pct ?? '',
            t.arrival_pct ?? '',
            t.completion_pct ?? '',
            t.avg_rating ?? '',
            t.sla_score ?? '',
        ],
        (month, v) => [
            month,
            'Vendor',
            v.vendor_name || '',
            v.jobs ?? 0,
            v.avg_response_sec ?? '',
            v.acceptance_pct ?? '',
            v.arrival_pct ?? '',
            v.completion_pct ?? '',
            v.avg_rating ?? '',
            v.sla_score ?? '',
        ],
    );
}

// Colour the SLA score cell so weak vendors stand out.
const slaClass = (v) => {
    if (v == null) return 'text-gray-400';
    if (v >= 80) return 'text-green-700';
    if (v >= 60) return 'text-amber-600';
    return 'text-red-600';
};

const Cols = ({ t }) => (
    <>
        <td className="px-3 py-2 text-right tabular-nums">{t.jobs}</td>
        <td className="px-3 py-2 text-right tabular-nums">{secs(t.avg_response_sec)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{pct(t.acceptance_pct)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{pct(t.arrival_pct)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{pct(t.completion_pct)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{num(t.avg_rating, 2)}</td>
        <td className={`px-3 py-2 text-right tabular-nums font-medium ${slaClass(t.sla_score)}`}>
            {num(t.sla_score)}
        </td>
    </>
);

const VendorSlaReport = () => {
    const [months, setMonths] = useState(12);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [open, setOpen] = useState({});

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        reportsService
            .getVendorSlaReport({ months })
            .then((res) => {
                setData(res);
                const first = res.months?.[0]?.month;
                setOpen(first ? { [first]: true } : {});
            })
            .catch((e) => setError(e?.message || 'Failed to load report'))
            .finally(() => setLoading(false));
    }, [months]);

    useEffect(() => { load(); }, [load]);

    const handleExport = () => {
        if (!data?.months?.length) return;
        downloadCsv(
            `vendor-sla-report-last-${months}-months.csv`,
            SLA_CSV_HEADERS,
            slaCsvRows(data),
        );
    };

    const g = data?.grand_totals;
    const canExport = !loading && !!data?.months?.length;

    return (
        <div className="p-6">
            <div className="flex items-center gap-2 mb-1">
                <Gauge className="w-6 h-6 text-gray-700" />
                <h1 className="text-xl font-semibold">Vendor SLA Report</h1>
            </div>
            <p className="text-sm text-gray-500 mb-4">
                Month-wise acceptance, arrival, completion, rating and SLA score
                from completed jobs, broken down by vendor. Excludes jobs marked
                out of SLA.
            </p>

            <div className="flex items-center gap-3 mb-4">
                <select
                    value={months}
                    onChange={(e) => setMonths(Number(e.target.value))}
                    className="border rounded-lg px-3 py-2 text-sm">
                    {RANGE_OPTS.map((o) => (
                        <option key={o.months} value={o.months}>{o.label}</option>
                    ))}
                </select>
                <button
                    onClick={load}
                    className="inline-flex items-center gap-1 text-sm px-3 py-2 border rounded-lg hover:bg-gray-50">
                    <RefreshCw className="w-4 h-4" /> Refresh
                </button>
                <button
                    type="button"
                    onClick={handleExport}
                    disabled={!canExport}
                    className="inline-flex items-center gap-1 text-sm px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    <Download className="w-4 h-4" /> Export CSV
                </button>
            </div>

            {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

            {loading ? (
                <LoadingSpinner />
            ) : !data || !data.months?.length ? (
                <div className="text-sm text-gray-400 border rounded-xl p-8 text-center">
                    No completed jobs with SLA metrics in this period.
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
                        {[
                            ['Jobs', g.jobs],
                            ['Acceptance', pct(g.acceptance_pct)],
                            ['On-time arrival', pct(g.arrival_pct)],
                            ['Completion', pct(g.completion_pct)],
                            ['Avg rating', num(g.avg_rating, 2)],
                            ['SLA score', num(g.sla_score)],
                        ].map(([label, val]) => (
                            <div key={label} className="border rounded-xl px-4 py-3">
                                <div className="text-xs text-gray-500">{label}</div>
                                <div className="text-lg font-semibold tabular-nums">{val}</div>
                            </div>
                        ))}
                    </div>

                    <div className="overflow-x-auto border rounded-xl">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-gray-500">
                                <tr>
                                    <th className="text-left px-3 py-2">Month / Vendor</th>
                                    <th className="text-right px-3 py-2">Jobs</th>
                                    <th className="text-right px-3 py-2">Avg response</th>
                                    <th className="text-right px-3 py-2">Acceptance</th>
                                    <th className="text-right px-3 py-2">On-time</th>
                                    <th className="text-right px-3 py-2">Completion</th>
                                    <th className="text-right px-3 py-2">Rating</th>
                                    <th className="text-right px-3 py-2">SLA score</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.months.map((m) => {
                                    const isOpen = !!open[m.month];
                                    return (
                                        <React.Fragment key={m.month}>
                                            <tr
                                                className="border-t bg-gray-50/60 font-medium cursor-pointer hover:bg-gray-100"
                                                onClick={() => setOpen((o) => ({ ...o, [m.month]: !o[m.month] }))}>
                                                <td className="px-3 py-2 flex items-center gap-1">
                                                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                    {m.month}
                                                </td>
                                                <Cols t={m.totals} />
                                            </tr>
                                            {isOpen && m.vendors.map((v) => (
                                                <tr key={`${m.month}-${v.vendor_id ?? 'none'}`} className="border-t">
                                                    <td className="px-3 py-2 pl-9 text-gray-600">{v.vendor_name}</td>
                                                    <Cols t={v} />
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default VendorSlaReport;
